import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  DossierPaginationFilters,
  getMyEmployerDossiers,
} from "../../api/dossier/getMyEmployerDossiers";
import APFilterableList from "../../components/APFilterableList";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import { APCardColor } from "../../components/cards/APCard";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import APTextField from "../../components/form/APTextField";
import {
  buildAutoCompleteOptions,
  buildAutoCompleteOptionsFromArray,
} from "../../helpers/buildOptions";
import {
  filterAccountsByEmployerPage,
  filterAccountsByPermission,
  isAuthorisedToViewEmployerPage,
} from "../../helpers/user";
import { useAuth } from "../../providers/AuthProvider";
import {
  Dossier,
  DossierStatus,
  DossierStatusGroupMap,
  DossierStatusLabelMap,
} from "../../types/Dossier";
import { User } from "../../types/User";
import DossierFilterSummary from "../components/DossierFilterSummary";
import EmployerDossierCard from "../components/EmployerDossierCard";

export type EmployerDossierFilterInputs = {
  accounts: AutoCompleteOption[] | null;
  dossierStatuses: AutoCompleteOption[] | null;
  employeeSearch: string;
};

const getStatusesFromQueryParam = (
  statusParam: string | null,
  allStatusOptions: AutoCompleteOption[]
): AutoCompleteOption[] => {
  if (!statusParam) {
    return allStatusOptions;
  }

  const requestedStatuses = statusParam
    .split(",")
    .map((status) => status.trim());
  const matchedOptions = allStatusOptions.filter((option) =>
    requestedStatuses.includes(option.id)
  );

  return matchedOptions.length > 0 ? matchedOptions : allStatusOptions;
};

export const DEFAULT_PAGE_SIZE = 20;

const accountColors: APCardColor[] = [
  "neutralWhite",
  "primary",
  "tertiaryGreen",
  "neutralGray",
  "tertiaryBlue",
  "tertiaryYellow",
  "tertiaryDarkBlue",
];

export const accountTextColorMap: Record<APCardColor, string | undefined> = {
  neutralWhite: undefined,
  primary: "primary.main",
  tertiaryGreen: "tertiaryGreen.main",
  neutralGray: "outline.main",
  tertiaryBlue: "tertiaryBlue.main",
  tertiaryYellow: "tertiaryYellow.main",
  tertiaryDarkBlue: "tertiaryDarkBlue.main",
};

const EmployerDossierList = ({ me }: { me: User }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getScopedPageUrl } = useAuth();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const [accountColorMap, setAccountColorMap] = useState<
    Record<string, APCardColor>
  >({});

  const authorisedAccounts = me.accountPermissions
    ? filterAccountsByEmployerPage(
        me.accountPermissions,
        "employerViewDossiers"
      )
    : [];
  const authorisedToTerminate = me.accountPermissions
    ? filterAccountsByPermission(me.accountPermissions, "terminateDossiers")
    : [];

  const accountOptions = useMemo(() => {
    const options = buildAutoCompleteOptions(authorisedAccounts, "name", "id");
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [authorisedAccounts]);
  const hideAccounts = accountOptions.length <= 1;
  const dossierStatusOptions = useMemo(
    () =>
      buildAutoCompleteOptionsFromArray(
        DossierStatusGroupMap.activated,
        DossierStatusLabelMap
      ),
    []
  );

  const defaultDossierStatusOptions = useMemo(
    () =>
      getStatusesFromQueryParam(
        searchParams.get("status"),
        dossierStatusOptions
      ),
    [searchParams, dossierStatusOptions]
  );

  const [filters, setFilters] = useState<DossierPaginationFilters>({
    pageSize: DEFAULT_PAGE_SIZE,
    accountIds: accountOptions.map((account) => account.id),
    dossierStatuses: defaultDossierStatusOptions.map(
      (status) => status.id as DossierStatus
    ),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EmployerDossierFilterInputs>({
    mode: "onTouched",
    defaultValues: {
      accounts: accountOptions,
      dossierStatuses: defaultDossierStatusOptions,
      employeeSearch: "",
    },
  });

  useEffect(() => {
    if (
      me.accountPermissions &&
      isAuthorisedToViewEmployerPage(
        me.accountPermissions,
        "employerViewDossiers"
      )
    ) {
      setNavigationCheck(true);
    } else {
      navigate(getScopedPageUrl("home"), { replace: true });
    }
  });

  const updateAccountColorMap = () => {
    const newColorMap = { ...accountColorMap };

    accountOptions.forEach((accountOption) => {
      // If account does not already have a color then assign it one
      if (!newColorMap[accountOption.id]) {
        const colorIndex = Object.keys(newColorMap).length % 7;
        newColorMap[accountOption.id] = accountColors[colorIndex];
      }
    });

    if (
      Object.keys(newColorMap).length !== Object.keys(accountColorMap).length
    ) {
      setAccountColorMap(newColorMap);
    }
  };

  const inputsToFilters = (inputs: EmployerDossierFilterInputs) => {
    return {
      ...filters,
      pageSize: DEFAULT_PAGE_SIZE,
      nextPageLink: undefined,
      accountIds: (inputs.accounts ?? accountOptions).map(
        (account) => account.id
      ),
      dossierStatuses: (inputs.dossierStatuses ?? dossierStatusOptions).map(
        (status) => status.id as DossierStatus
      ),
      employeeSearch: inputs.employeeSearch || undefined,
    };
  };

  const renderItems = (items: Dossier[]) => {
    const dossiers = items.map((dossier) => (
      <Box key={dossier.id}>
        <EmployerDossierCard
          dossier={dossier}
          color={
            dossier.opportunity?.parentAccountId
              ? accountColorMap[dossier.opportunity.parentAccountId]
              : accountColorMap[0]
          }
          canTerminate={authorisedToTerminate.some(
            (account) => account.id === dossier.opportunity?.parentAccountId
          )}
          canViewTermination={authorisedAccounts.some(
            (account) => account.id === dossier.opportunity?.parentAccountId
          )}
          hideAccount={hideAccounts}
        />
      </Box>
    ));

    return (
      <Stack
        sx={{
          display: "grid",
          gap: "16px",
          gridTemplateColumns: { md: "1fr 1fr" },
        }}
      >
        {dossiers}
      </Stack>
    );
  };

  const renderFilterSummary = () => {
    const filteredAccounts = accountOptions.filter((account) =>
      filters.accountIds?.includes(account.id)
    );
    const filteredDossierStatuses = dossierStatusOptions.filter((status) =>
      filters.dossierStatuses?.includes(status.id as DossierStatus)
    );

    return (
      <DossierFilterSummary
        accounts={filteredAccounts}
        dossierStatuses={filteredDossierStatuses}
        employeeSearch={filters.employeeSearch || ""}
        accountColorMap={accountColorMap}
        hideAccounts={hideAccounts}
      />
    );
  };

  return (
    <APFilterableList
      title="Novated Leases"
      filters={filters}
      setFilters={setFilters}
      pageSize={DEFAULT_PAGE_SIZE}
      getItems={getMyEmployerDossiers}
      inputsToFilters={inputsToFilters}
      handleSubmit={handleSubmit}
      errors={errors}
      abovePaginationContent={renderFilterSummary()}
      formFields={
        <>
          {!hideAccounts && (
            <APAutoComplete
              name="accounts"
              label="Employers"
              options={accountOptions}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={accountOptions}
              setValue={setValue}
              multiple
            />
          )}

          <APAutoComplete
            name="dossierStatuses"
            label="Status"
            options={dossierStatusOptions}
            control={control}
            validations={{ required: true }}
            errors={errors}
            defaultValue={defaultDossierStatusOptions}
            setValue={setValue}
            multiple
          />

          <APTextField
            name="employeeSearch"
            label="Search"
            control={control}
            defaultValue=""
            validations={{}}
            errors={errors}
            setValue={setValue}
            clearable={true}
          />
        </>
      }
      renderItems={renderItems}
      isLoading={!navigationCheckComplete}
      isSuccess={navigationCheckComplete}
      onPageLoad={updateAccountColorMap}
      bottomContent={
        <Stack
          spacing={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            pt: 2,
          }}
        >
          <Box sx={{ display: "flex", columnGap: 1, alignItems: "center" }}>
            <BackButton />
            <HomeButton />
          </Box>
          <APContactHelpButton />
        </Stack>
      }
      autoExpandFiltersOnMultiPage={true}
    />
  );
};

export default EmployerDossierList;
